import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  X, 
  ZoomIn, 
  ZoomOut, 
  BookOpen, 
  Sun, 
  Moon,
  ChevronLeft,
  ChevronRight,
  Bookmark,
  MessageCircle,
  Maximize2,
  Minimize2,
  List,
  Sparkles,
  Send
} from 'lucide-react';
import apiService from '../services/api';

const Reader = ({ 
  isOpen, 
  onClose, 
  bookId, 
  initialChapter = 1,
  isTextbook = false, // Flag to indicate if this is a textbook (not a book)
  // Simple content mode props (for research papers/thesis)
  title,
  author,
  content
}) => {
  const [fontSize, setFontSize] = useState(18);
  const [theme, setTheme] = useState('night');
  const [fontFamily, setFontFamily] = useState('serif'); // 'serif' or 'sans'
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [aiError, setAiError] = useState(null);
  const [aiActiveAction, setAiActiveAction] = useState(null);
  const [bookmarks, setBookmarks] = useState([]);
  const [showChapterList, setShowChapterList] = useState(false);
  const [scrollPercent, setScrollPercent] = useState(0);
  const [isPageTurning, setIsPageTurning] = useState(false);

  // Floating Popover Tooltip
  const [showPopover, setShowPopover] = useState(false);
  const [popoverCoords, setPopoverCoords] = useState({ top: 0, left: 0 });

  // Interactive Chat
  const [chatHistory, setChatHistory] = useState([]);
  const [chatMessage, setChatMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(false);



  // Chapter-related state
  const [currentChapter, setCurrentChapter] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [bookInfo, setBookInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const contentRef = useRef(null);

  // Determine mode: simple content (research/thesis) or book with chapters
  const isSimpleMode = !bookId && (title || content);

  // Theme configurations for icons/borders
  const themes = {
    light: { name: 'Light', bg: '#ffffff', accent: '#0284c7' },
    dark: { name: 'Dark', bg: '#1a1a1a', accent: '#fb923c' },
    sepia: { name: 'Sepia', bg: '#f4ecd8', accent: '#8b5a2b' },
    night: { name: 'Night', bg: '#0d1117', accent: '#58a6ff' }
  };

  const fetchBookWithChapters = useCallback(async () => {
    try {
      setLoading(true);
      let data;
      if (isTextbook) {
        data = await apiService.getTextbookWithChapters(bookId);
      } else {
        data = await apiService.getBookWithChapters(bookId);
      }
      setBookInfo(data);
      setChapters(data.chapters || []);
      setError(null);
    } catch (err) {
      setError(`Failed to load ${isTextbook ? 'textbook' : 'book'} chapters`);
      console.error(`Error fetching ${isTextbook ? 'textbook' : 'book'} with chapters:`, err);
    } finally {
      setLoading(false);
    }
  }, [bookId, isTextbook]);

  const fetchChapter = useCallback(async (chapterNumber) => {
    try {
      setLoading(true);
      let data;
      if (isTextbook) {
        // For textbooks, find the chapter ID from the chapters list
        const textbookData = await apiService.getTextbookWithChapters(bookId);
        const chaptersList = textbookData.chapters || [];
        setChapters(chaptersList);
        const chapter = chaptersList.find(c => c.chapterNumber === chapterNumber);
        if (!chapter) {
          throw new Error(`Chapter ${chapterNumber} not found`);
        }
        data = await apiService.getTextbookChapterById(chapter.id);
      } else {
        data = await apiService.getChapter(bookId, chapterNumber);
      }
      setCurrentChapter(data);
      setError(null);
    } catch (err) {
      setError('Failed to load chapter');
      console.error('Error fetching chapter:', err);
    } finally {
      setLoading(false);
    }
  }, [bookId, isTextbook]);

  // Fetch book with chapters on mount (only in book mode)
  useEffect(() => {
    if (isOpen && bookId && !isSimpleMode) {
      fetchBookWithChapters();
    }
  }, [isOpen, bookId, isSimpleMode, fetchBookWithChapters]);

  // Fetch specific chapter when chapter number changes (only in book mode)
  useEffect(() => {
    if (isOpen && bookId && initialChapter && !isSimpleMode) {
      fetchChapter(initialChapter);
    }
  }, [isOpen, bookId, initialChapter, isSimpleMode, fetchChapter]);

  // In simple mode, set loading to false immediately
  useEffect(() => {
    if (isOpen && isSimpleMode) {
      setLoading(false);
    }
  }, [isOpen, isSimpleMode]);

  // Handle smooth scroll and page turn transition when chapter changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setScrollPercent(0);
    setIsPageTurning(true);
    const timer = setTimeout(() => {
      setIsPageTurning(false);
    }, 350);
    return () => clearTimeout(timer);
  }, [currentChapter]);

  // Handle scroll events to track reading progress
  const handleScroll = () => {
    const element = contentRef.current;
    if (!element) return;
    const totalHeight = element.scrollHeight - element.clientHeight;
    if (totalHeight <= 0) {
      setScrollPercent(0);
      return;
    }
    const scrollRatio = element.scrollTop / totalHeight;
    setScrollPercent(Math.round(scrollRatio * 100));
  };

  const handleNextChapter = useCallback(async () => {
    if (!currentChapter) return;
    
    try {
      setLoading(true);
      let data;
      if (isTextbook) {
        const currentChapterNum = currentChapter.chapterNumber;
        const chaptersList = chapters.length > 0 ? chapters : (await apiService.getTextbookWithChapters(bookId)).chapters || [];
        if (chapters.length === 0) {
          setChapters(chaptersList);
        }
        const currentIndex = chaptersList.findIndex(c => c.chapterNumber === currentChapterNum);
        if (currentIndex === -1 || currentIndex === chaptersList.length - 1) {
          return; // No next chapter
        }
        const nextChapter = chaptersList[currentIndex + 1];
        data = await apiService.getTextbookChapterById(nextChapter.id);
      } else {
        if (!currentChapter.hasNextChapter) return;
        data = await apiService.getNextChapter(bookId, currentChapter.chapterNumber);
      }
      setCurrentChapter(data);
      setError(null);
    } catch (err) {
      setError('Failed to load next chapter');
      console.error('Error fetching next chapter:', err);
    } finally {
      setLoading(false);
    }
  }, [bookId, currentChapter, isTextbook, chapters]);

  const handlePreviousChapter = useCallback(async () => {
    if (!currentChapter) return;
    
    try {
      setLoading(true);
      let data;
      if (isTextbook) {
        const currentChapterNum = currentChapter.chapterNumber;
        const chaptersList = chapters.length > 0 ? chapters : (await apiService.getTextbookWithChapters(bookId)).chapters || [];
        if (chapters.length === 0) {
          setChapters(chaptersList);
        }
        const currentIndex = chaptersList.findIndex(c => c.chapterNumber === currentChapterNum);
        if (currentIndex <= 0) {
          return; // No previous chapter
        }
        const prevChapter = chaptersList[currentIndex - 1];
        data = await apiService.getTextbookChapterById(prevChapter.id);
      } else {
        if (!currentChapter.hasPreviousChapter) return;
        data = await apiService.getPreviousChapter(bookId, currentChapter.chapterNumber);
      }
      setCurrentChapter(data);
      setError(null);
    } catch (err) {
      setError('Failed to load previous chapter');
      console.error('Error fetching previous chapter:', err);
    } finally {
      setLoading(false);
    }
  }, [bookId, currentChapter, isTextbook, chapters]);

  const handleChapterSelect = (chapterNumber) => {
    fetchChapter(chapterNumber);
    setShowChapterList(false);
  };

  // Handle text selection for AI assistance
  const handleTextSelection = () => {
    const selection = window.getSelection();
    const text = selection.toString().trim();
    if (text) {
      setSelectedText(text);
      
      // Calculate coordinates for floating capsule popover
      try {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        
        // Find relative positioning
        const container = document.querySelector('.reader-main');
        const containerRect = container ? container.getBoundingClientRect() : { top: 0, left: 0 };
        
        const top = rect.top - containerRect.top + (container ? container.scrollTop : 0) - 50;
        const left = rect.left - containerRect.left + (container ? container.scrollLeft : 0) + (rect.width / 2) - 100;
        
        setPopoverCoords({
          top: Math.max(10, top),
          left: Math.max(10, left)
        });
        setShowPopover(true);
      } catch (err) {
        console.error('Error calculating selection coords:', err);
        setShowAIAssistant(true);
        setAiResponse(null);
        setAiError(null);
        setAiActiveAction(null);
      }
    } else {
      setShowPopover(false);
    }
  };

  // Perform AI analyzer requests
  const handleAiAction = async (actionType, overrideText = null) => {
    const textToAnalyze = overrideText || selectedText;
    if (!textToAnalyze) return;
    
    setAiLoading(true);
    setAiError(null);
    setAiResponse(null);
    setAiActiveAction(actionType);
    setShowAIAssistant(true);
    setShowPopover(false);
    
    try {
      const response = await apiService.analyzeReaderText(textToAnalyze, actionType);
      setAiResponse(response);
      
      const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      
      // Initialize chat history with first response
      setChatHistory([
        {
          role: 'assistant',
          content: response,
          timestamp
        }
      ]);
      

      
    } catch (err) {
      console.error('Error running reader AI analysis:', err);
      setAiError(err.message || 'Failed to analyze text. Please verify configuration.');
    } finally {
      setAiLoading(false);
    }
  };

  // Send a follow-up chat question
  const handleSendChatMessage = async (e) => {
    if (e) e.preventDefault();
    if (!chatMessage.trim() || chatLoading || aiLoading) return;
    
    const userMsg = chatMessage.trim();
    setChatMessage('');
    setChatLoading(true);
    setAiError(null);
    
    const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
    const updatedHistory = [
      ...chatHistory,
      { role: 'user', content: userMsg, timestamp }
    ];
    setChatHistory(updatedHistory);
    
    try {
      const historyDto = chatHistory.map(h => ({
        role: h.role,
        content: h.content
      }));
      
      const response = await apiService.chatReaderText(
        selectedText,
        aiActiveAction || 'explain',
        historyDto,
        userMsg
      );
      
      setChatHistory([
        ...updatedHistory,
        {
          role: 'assistant',
          content: response,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]);
      setAiResponse(response);
    } catch (err) {
      console.error('Error sending chat message:', err);
      setAiError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setChatLoading(false);
    }
  };



  // Helper to format response text with list and bold markdown support
  const renderFormattedText = (text) => {
    if (!text) return null;
    const lines = text.split('\n');
    return lines.map((line, idx) => {
      // Headers
      if (line.startsWith('### ')) {
        return <h5 key={idx} style={{ marginTop: '0.75rem', marginBottom: '0.5rem', fontWeight: 600, color: themes[theme].accent }}>{line.replace('### ', '')}</h5>;
      }
      if (line.startsWith('## ') || line.startsWith('# ')) {
        return <h4 key={idx} style={{ marginTop: '0.75rem', marginBottom: '0.5rem', fontWeight: 600, color: themes[theme].accent }}>{line.replace(/##? /, '')}</h4>;
      }
      
      const trimmedLine = line.trim();
      
      // Bullets/List items
      if (trimmedLine.startsWith('* ') || trimmedLine.startsWith('- ')) {
        const listText = trimmedLine.replace(/^[*-\s]+/, '');
        const parts = listText.split(/(\*\*[^*]+\*\*)/g);
        const lineContent = parts.map((part, pIdx) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={pIdx} style={{ fontWeight: 600 }}>{part.slice(2, -2)}</strong>;
          }
          return part;
        });
        return (
          <li key={idx} style={{ marginLeft: '1.25rem', marginBottom: '0.35rem', listStyleType: 'disc' }}>
            {lineContent}
          </li>
        );
      }
      
      // Normal text lines
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      const lineContent = parts.map((part, pIdx) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={pIdx} style={{ fontWeight: 600 }}>{part.slice(2, -2)}</strong>;
        }
        return part;
      });

      return (
        <div key={idx} style={{ minHeight: '1.2em', marginBottom: '0.5rem' }}>
          {lineContent}
        </div>
      );
    });
  };

  // Toggle bookmark
  const toggleBookmark = () => {
    if (!currentChapter) return;
    const chapterNum = currentChapter.chapterNumber;
    if (bookmarks.includes(chapterNum)) {
      setBookmarks(bookmarks.filter(num => num !== chapterNum));
    } else {
      setBookmarks([...bookmarks, chapterNum]);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePreviousChapter();
      if (e.key === 'ArrowRight') handleNextChapter();
      if (e.key === '+' && e.ctrlKey) {
        e.preventDefault();
        setFontSize(prev => Math.min(32, prev + 2));
      }
      if (e.key === '-' && e.ctrlKey) {
        e.preventDefault();
        setFontSize(prev => Math.max(12, prev - 2));
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, onClose, currentChapter, handleNextChapter, handlePreviousChapter]);

  if (!isOpen) return null;

  return (
    <div className={`reader-overlay ${isFullscreen ? 'fullscreen' : ''}`}>
      <div className={`reader-container theme-${theme}`}>
        
        {/* Top Reading Progress Bar */}
        <div className="reader-progress-bar-container">
          <div 
            className="reader-progress-bar-fill"
            style={{ width: `${scrollPercent}%` }}
          />
        </div>

        {/* Toolbar Header */}
        <div className="reader-header">
          <div className="reader-title-section">
            <BookOpen size={20} style={{ color: themes[theme].accent }} />
            <div>
              <h3>
                {isSimpleMode 
                  ? title 
                  : (currentChapter ? currentChapter.bookTitle : bookInfo?.title || 'Loading...')}
              </h3>
              {isSimpleMode && author && (
                <p>By {author}</p>
              )}
              {!isSimpleMode && currentChapter && (
                <p>
                  Chapter {currentChapter.chapterNumber}: {currentChapter.chapterTitle}
                </p>
              )}
            </div>
          </div>
          
          <div className="reader-controls">
            {/* Chapter List Toggle - Only show in book mode */}
            {!isSimpleMode && (
              <button 
                className={`control-btn ${showChapterList ? 'active' : ''}`}
                onClick={() => setShowChapterList(!showChapterList)}
                title="Chapter list"
              >
                <List size={18} />
              </button>
            )}

            {/* Font Family Selector */}
            <button
              className="font-family-toggle"
              onClick={() => setFontFamily(prev => prev === 'serif' ? 'sans' : 'serif')}
              title="Toggle reading font family"
            >
              {fontFamily === 'serif' ? 'Classical Serif' : 'Modern Sans'}
            </button>

            {/* Theme Selector */}
            <div className="theme-selector">
              {Object.keys(themes).map((themeKey) => (
                <button
                  key={themeKey}
                  className={`theme-btn ${theme === themeKey ? 'active' : ''}`}
                  onClick={() => setTheme(themeKey)}
                  title={`${themes[themeKey].name} theme`}
                  style={{
                    backgroundColor: themes[themeKey].bg,
                    border: theme === themeKey ? `2px solid ${themes[themeKey].accent}` : '2px solid transparent'
                  }}
                >
                  {themeKey === 'light' && <Sun size={14} style={{ color: '#000' }} />}
                  {themeKey === 'dark' && <Moon size={14} style={{ color: '#fb923c' }} />}
                  {themeKey === 'sepia' && <Sun size={14} style={{ color: '#8b5a2b' }} />}
                  {themeKey === 'night' && <Moon size={14} style={{ color: '#58a6ff' }} />}
                </button>
              ))}
            </div>

            {/* Font Size Controls */}
            <div className="font-controls">
              <button 
                onClick={() => setFontSize(prev => Math.max(12, prev - 2))}
                title="Decrease font size"
              >
                <ZoomOut size={16} />
              </button>
              <span>{fontSize}px</span>
              <button 
                onClick={() => setFontSize(prev => Math.min(32, prev + 2))}
                title="Increase font size"
              >
                <ZoomIn size={16} />
              </button>
            </div>

            {/* Bookmark button - Only show in book mode */}
            {!isSimpleMode && (
              <button 
                className={`control-btn ${currentChapter && bookmarks.includes(currentChapter.chapterNumber) ? 'active' : ''}`}
                onClick={toggleBookmark}
                title="Bookmark this chapter"
                style={{ color: currentChapter && bookmarks.includes(currentChapter.chapterNumber) ? themes[theme].accent : 'inherit' }}
              >
                <Bookmark size={18} fill={currentChapter && bookmarks.includes(currentChapter.chapterNumber) ? 'currentColor' : 'none'} />
              </button>
            )}

            {/* Fullscreen Toggle */}
            <button 
              className="control-btn"
              onClick={() => setIsFullscreen(!isFullscreen)}
              title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>

            {/* Close Button */}
            <button 
              className="control-btn close-btn"
              onClick={onClose}
              title="Close reader"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="reader-main">
          
          {/* Collapsible Left Chapter List Sidebar - Only in book mode */}
          {!isSimpleMode && showChapterList && (
            <div className="chapter-list-panel">
              <div className="chapter-list-header">
                <h4>Chapters</h4>
                <button 
                  onClick={() => setShowChapterList(false)}
                  className="control-btn"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="chapter-list-content">
                {chapters.map((chapter) => (
                  <button
                    key={chapter.id}
                    className={`chapter-item ${currentChapter?.chapterNumber === chapter.chapterNumber ? 'active' : ''}`}
                    onClick={() => handleChapterSelect(chapter.chapterNumber)}
                    style={{
                      borderLeftColor: bookmarks.includes(chapter.chapterNumber) 
                        ? themes[theme].accent 
                        : 'transparent'
                    }}
                  >
                    <div className="chapter-number">Ch. {chapter.chapterNumber}</div>
                    <div className="chapter-info">
                      <div className="chapter-title">{chapter.chapterTitle}</div>
                      {chapter.chapterSubtitle && (
                        <div className="chapter-subtitle">
                          {chapter.chapterSubtitle}
                        </div>
                      )}
                      {chapter.readingTimeMinutes && (
                        <div className="chapter-meta">
                          ⏱️ {chapter.readingTimeMinutes} min read
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Scrolling Reading Pane */}
          <div 
            className={`reader-content font-${fontFamily}`}
            style={{ fontSize: `${fontSize}px` }}
            onMouseUp={handleTextSelection}
            onScroll={handleScroll}
            ref={contentRef}
          >
            {loading ? (
              <div className="reader-loading">
                <div className="loader" style={{ marginBottom: '15px' }}></div>
                <p>Loading chapter...</p>
              </div>
            ) : error ? (
              <div className="reader-error">
                <p style={{ color: '#ef4444' }}>⚠️ {error}</p>
                {!isSimpleMode && (
                  <button className="btn btn-primary btn-small" onClick={() => fetchChapter(initialChapter)} style={{ marginTop: '1rem' }}>
                    Retry
                  </button>
                )}
              </div>
            ) : isSimpleMode ? (
              /* Simple content mode - for research papers/thesis */
              content ? (
                <div className={`reader-page ${isPageTurning ? 'page-turning' : ''}`}>
                  <div 
                    className="chapter-content"
                    dangerouslySetInnerHTML={{ __html: content }}
                  />
                </div>
              ) : (
                <div className="reader-empty">
                  <p>No content available</p>
                </div>
              )
            ) : currentChapter ? (
              /* Book mode - with chapters */
              <div className={`reader-page ${isPageTurning ? 'page-turning' : ''}`}>
                <div className="chapter-header">
                  <h2 style={{ color: themes[theme].accent }}>
                    Chapter {currentChapter.chapterNumber}: {currentChapter.chapterTitle}
                  </h2>
                  {currentChapter.chapterSubtitle && (
                    <h3>
                      {currentChapter.chapterSubtitle}
                    </h3>
                  )}
                  {currentChapter.readingTimeMinutes && (
                    <p className="reading-time">
                      Estimated reading time: {currentChapter.readingTimeMinutes} minutes
                    </p>
                  )}
                </div>
                <div 
                  className="chapter-content"
                  dangerouslySetInnerHTML={{ __html: currentChapter.contentHtml }}
                />
              </div>
            ) : (
              <div className="reader-empty">
                <p>No chapter selected</p>
              </div>
            )}
          </div>

          {/* AI Assistant Sidebar Panel */}
          {showAIAssistant && (
            <div className="ai-assistant-panel">
              <div className="ai-header">
                <div>
                  <MessageCircle size={20} style={{ color: themes[theme].accent }} />
                  <h4>Vedic AI Assist</h4>
                </div>
                <button 
                  onClick={() => {
                    setShowAIAssistant(false);
                    setSelectedText('');
                    setAiResponse(null);
                    setAiError(null);
                    setAiActiveAction(null);
                    setChatHistory([]);
                  }}
                  className="control-btn"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="ai-content" style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                {(chatHistory.length === 0 && !aiLoading && !aiError) ? (
                  <>
                    {selectedText && (
                      <div className="selected-text" style={{ borderColor: themes[theme].accent }}>
                        <p style={{ color: themes[theme].accent }}>Selected text:</p>
                        <p>"{selectedText}"</p>
                      </div>
                    )}

                    <div className="ai-suggestions">
                      <button 
                        className="ai-action-btn"
                        onClick={() => handleAiAction('explain')}
                        disabled={aiLoading}
                        style={{ 
                          backgroundColor: themes[theme].accent + '25',
                          color: themes[theme].accent,
                          opacity: aiLoading ? 0.6 : 1
                        }}
                      >
                        📖 Explain this concept
                      </button>
                      <button 
                        className="ai-action-btn"
                        onClick={() => handleAiAction('translate')}
                        disabled={aiLoading}
                        style={{ 
                          backgroundColor: themes[theme].accent + '25',
                          color: themes[theme].accent,
                          opacity: aiLoading ? 0.6 : 1
                        }}
                      >
                        🔄 Translate Sanskrit terms
                      </button>
                      <button 
                        className="ai-action-btn"
                        onClick={() => handleAiAction('cross_reference')}
                        disabled={aiLoading}
                        style={{ 
                          backgroundColor: themes[theme].accent + '25',
                          color: themes[theme].accent,
                          opacity: aiLoading ? 0.6 : 1
                        }}
                      >
                        🔗 Search cross-references
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="ai-response" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', border: 'none', background: 'transparent', padding: 0 }}>
                    <div className="ai-chat-history-container" style={{ flex: 1 }}>
                      {selectedText && (
                        <div className="chat-selected-context-quote" style={{
                          borderLeft: `3px solid ${themes[theme].accent}`,
                          padding: '0.75rem 1rem',
                          fontSize: '0.88rem',
                          background: themes[theme].accent + '10',
                          borderRadius: '8px',
                          marginBottom: '1rem',
                          fontStyle: 'italic',
                          color: themes[theme].accent,
                          lineHeight: '1.5'
                        }}>
                          <span style={{ fontWeight: 600, fontStyle: 'normal', display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem', opacity: 0.85 }}>
                            Selected Context
                          </span>
                          "{selectedText}"
                        </div>
                      )}

                      {chatHistory.map((chat, idx) => (
                        <div key={idx} className={`ai-chat-bubble ${chat.role}`}>
                          <span className="ai-chat-bubble-meta">
                            {chat.role === 'user' ? 'You' : 'Vedic AI'} • {chat.timestamp}
                          </span>
                          <div>
                            {renderFormattedText(chat.content)}
                          </div>
                        </div>
                      ))}

                      {aiLoading && (
                        <div className="ai-pulse-loader">
                          <div className="ai-pulse-line short"></div>
                          <div className="ai-pulse-line medium"></div>
                          <div className="ai-pulse-line"></div>
                          <p style={{ fontSize: '0.8rem', opacity: 0.6, margin: '4px 0 0' }}>Vedic AI is analyzing...</p>
                        </div>
                      )}

                      {chatLoading && (
                        <div className="ai-pulse-loader" style={{ alignSelf: 'flex-start' }}>
                          <div className="ai-pulse-line short"></div>
                          <div className="ai-pulse-line"></div>
                          <p style={{ fontSize: '0.8rem', opacity: 0.6, margin: '4px 0 0' }}>Vedic AI is typing...</p>
                        </div>
                      )}
                      
                      {aiError && (
                        <div style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                          <p style={{ fontWeight: '600', margin: '0 0 0.25rem' }}>⚠️ Error:</p>
                          <p style={{ margin: 0 }}>{aiError}</p>
                        </div>
                      )}
                    </div>

                    {/* Chat Input Bar */}
                    {aiResponse && !aiLoading && (
                      <form onSubmit={handleSendChatMessage} className="ai-chat-input-container" style={{ borderTop: '1px solid rgba(255,255,255,0.08)', padding: '0.75rem 0 0' }}>
                        <input 
                          type="text"
                          placeholder="Ask a follow-up question..."
                          value={chatMessage}
                          onChange={(e) => setChatMessage(e.target.value)}
                          className="ai-chat-input"
                          disabled={chatLoading}
                        />
                        <button 
                          type="submit" 
                          className="ai-chat-send-btn" 
                          disabled={chatLoading || !chatMessage.trim()}
                          style={{ backgroundColor: themes[theme].accent }}
                        >
                          <Send size={14} />
                        </button>
                      </form>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Floating AI Tooltip Popover */}
          {showPopover && selectedText && (
            <div 
              className="ai-tooltip-popover"
              style={{
                top: `${popoverCoords.top}px`,
                left: `${popoverCoords.left}px`
              }}
            >
              <button 
                className="ai-tooltip-btn"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleAiAction('explain');
                }}
              >
                <Sparkles size={13} style={{ color: themes[theme].accent }} /> Explain
              </button>
              <button 
                className="ai-tooltip-btn"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleAiAction('translate');
                }}
              >
                🔄 Translate
              </button>
              <button 
                className="ai-tooltip-btn"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleAiAction('cross_reference');
                }}
              >
                🔗 Cross-ref
              </button>
              <button
                className="ai-tooltip-btn"
                style={{ borderLeft: '1px solid rgba(255,255,255,0.15)', padding: '6px 8px' }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  setShowPopover(false);
                }}
              >
                <X size={13} />
              </button>
            </div>
          )}
        </div>

        {/* Footer Navigation - Only show in book mode */}
        {!isSimpleMode && (
          <div className="reader-footer">
            <div className="page-navigation">
              <button 
                onClick={handlePreviousChapter}
                disabled={!currentChapter || !currentChapter.hasPreviousChapter || loading}
                style={{ opacity: (!currentChapter || !currentChapter.hasPreviousChapter || loading) ? 0.4 : 1 }}
              >
                <ChevronLeft size={18} />
                Previous Chapter
              </button>
              
              <div className="page-info">
                {currentChapter ? (
                  <>Chapter {currentChapter.chapterNumber} of {bookInfo?.totalChapters || chapters.length}</>
                ) : (
                  <>Loading...</>
                )}
              </div>
              
              <button 
                onClick={handleNextChapter}
                disabled={!currentChapter || !currentChapter.hasNextChapter || loading}
                style={{ opacity: (!currentChapter || !currentChapter.hasNextChapter || loading) ? 0.4 : 1 }}
              >
                Next Chapter
                <ChevronRight size={18} />
              </button>
            </div>

            <div className="reader-shortcuts">
              <span>Shortcuts: ← → Navigate | Ctrl + / - Zoom | Esc Close</span>
            </div>
          </div>
        )}
        
        {/* Simple footer for simple mode */}
        {isSimpleMode && (
          <div className="reader-footer" style={{ justifyContent: 'center' }}>
            <div className="reader-shortcuts">
              <span>Shortcuts: Ctrl + / - Zoom | Esc Close</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reader;
